import { withLoading } from "@/api/api";
import { get } from "./http";

/**
 * Service da tela `apuracao-por-projeto.vue` (API real).
 * `GET .../apuracao/projetos` devolve todos os projetos apurados do ano-base (um benefício
 * por vez) e o total geral; **não é paginada** — a paginação da tela é local.
 *
 * @typedef {object} ApuracaoLinha
 * @property {number} id - id do projeto
 * @property {string} projeto - nome do projeto
 * @property {number} total - total apurado
 * @property {number} pesquisa - valor em Pesquisa (despesa)
 * @property {number} desenvolvimento - valor em Desenvolvimento (ativo)
 * @property {number} semFase - valor ainda sem fase
 * @property {Array<{id: string, grupo: string, total: number, pesquisa: number,
 *   desenvolvimento: number, semFase: number}>} grupos - abertura (RH · terceiros · materiais)
 */

/** Benefícios usados no seletor LB × LI (`trabalho`). */
export const BENEFICIOS = [
  { text: "Lei do Bem", value: 1 },
  { text: "Lei de Informática", value: 2 },
];

/** Rótulos dos grupos, na ordem exibida na abertura da linha. */
export const ROTULOS_GRUPO = {
  rh: "Recursos Humanos",
  terceiros: "Serviços de Terceiros",
  materiais: "Materiais",
};

/** Grupos da API, na ordem da tela (chave do front → campo da resposta). */
const GRUPOS_API = [
  { chave: "rh", campo: "recursos_humanos" },
  { chave: "terceiros", campo: "servicos_terceiros" },
  { chave: "materiais", campo: "materiais" },
];

const LOADING_KEY = "apuracaocapex-apuracao-projeto";

/** Número seguro. */
const numero = (valor) => Number(valor) || 0;

/** Texto de filtro, sem espaços nas pontas. */
const texto = (valor) => String(valor || "").trim();

/** `MM/AAAA` (formato da tela) → `AAAA-MM` (formato da API). */
const competenciaParaApi = (competencia) => {
  const partes = String(competencia || "").match(/^(\d{2})\/(\d{4})$/);

  return partes ? `${partes[2]}-${partes[1]}` : "";
};

/** Benefícios (trabalho) que o projeto atende — 3 = os dois. */
export const trabalhosDoProjeto = (trabalho) => {
  const valor = Number(trabalho);

  if (valor === 1) return [1];
  if (valor === 2) return [2];
  if (valor === 3) return [1, 2];

  return [];
};

/** Um grupo da resposta no formato da abertura da linha. */
const montarGrupo = (chave, dados) => ({
  id: chave,
  grupo: ROTULOS_GRUPO[chave],
  total: numero(dados.total),
  pesquisa: numero(dados.pesquisa),
  desenvolvimento: numero(dados.desenvolvimento),
  semFase: numero(dados.sem_fase),
});

/** Uma linha da tela a partir de um projeto da API. */
const montarLinha = (projeto) => ({
  id: projeto.projeto_id,
  projeto: projeto.nome,
  total: numero(projeto.total),
  pesquisa: numero(projeto.pesquisa),
  desenvolvimento: numero(projeto.desenvolvimento),
  semFase: numero(projeto.sem_fase),
  grupos: GRUPOS_API.map(({ chave, campo }) => montarGrupo(chave, (projeto.grupos || {})[campo] || {})),
});

/** Total geral (cartões e linha de totais) no formato da tela. */
const montarTotais = (total = {}) => ({
  total: numero(total.total),
  pesquisa: numero(total.pesquisa),
  desenvolvimento: numero(total.desenvolvimento),
  semFase: numero(total.sem_fase),
});

/**
 * Coluna da tela → campo aceito na ordenação da API. O back-end aceita **só** `nome`,
 * `total`, `pesquisa`, `desenvolvimento` e `sem_fase` — outro `sort[...]` é ignorado
 * (por isso a tela usa `projeto`/`semFase` e aqui traduzimos para `nome`/`sem_fase`).
 */
const CAMPOS_ORDENACAO = {
  projeto: "nome",
  total: "total",
  pesquisa: "pesquisa",
  desenvolvimento: "desenvolvimento",
  semFase: "sem_fase",
};

/** Ordenação do `v-data-table` (`[{field, desc}]`) no formato da API (`{campo: "asc"}`). */
const montarSort = (sort) => (Array.isArray(sort)
  ? sort.reduce((acumulado, item) => {
    const campo = item && CAMPOS_ORDENACAO[item.field];

    return campo ? { ...acumulado, [campo]: item.desc ? "desc" : "asc" } : acumulado;
  }, {})
  : sort);

/**
 * Apuração por projeto do ano-base, para um benefício.
 *
 * @param {{ empresaId: number|string, anoBase: string|number, beneficio?: number,
 *   competenciaIni?: string|null, competenciaFim?: string|null,
 *   filtros?: { projeto?: string }, sort?: Array|object }} params
 *   competências em **`AAAA-MM`** (a tela usa `MM/AAAA` e converte no `listarApuracao`).
 * @returns {Promise<{ rows: ApuracaoLinha[], totais: object, periodo: object }>}
 */
export const obterApuracao = async ({
  empresaId,
  anoBase,
  beneficio = 1,
  competenciaIni = null,
  competenciaFim = null,
  filtros = {},
  sort = [],
}) =>
  withLoading(LOADING_KEY, async () => {
    const busca = texto(filtros.projeto);

    const corpo = await get(empresaId, "apuracao/projetos", {
      ano_base: Number(anoBase) || undefined,
      trabalho: beneficio,
      competencia_ini: competenciaIni || undefined,
      competencia_fim: competenciaFim || undefined,
      filters: { nome: busca ? [busca] : [] },
      sort: montarSort(sort),
    });

    const dados = (corpo && corpo.data) || {};

    return {
      rows: (dados.projetos || []).map(montarLinha),
      totais: montarTotais(dados.total),
      periodo: { ini: dados.competencia_ini || "", fim: dados.competencia_fim || "" },
    };
  });

/**
 * Compatibilidade com a tela atual: mesma assinatura do antigo `listarApuracao` — como a API
 * traz todos os projetos, a **paginação é local** (a ordenação vai para a API, que a suporta).
 *
 * @returns {Promise<{ rows: ApuracaoLinha[], total: number, page: number, perPage: number,
 *   totais: object }>}
 */
export const listarApuracao = async ({
  empresaId,
  anoBase,
  beneficio = 1,
  page = 1,
  perPage = 25,
  sort = [],
  filtros = {},
} = {}) => {
  const periodo = filtros.periodo || [];
  const { rows, totais } = await obterApuracao({
    empresaId,
    anoBase,
    beneficio,
    competenciaIni: competenciaParaApi(periodo[0]),
    competenciaFim: competenciaParaApi(periodo[1]),
    filtros,
    sort,
  });

  const inicio = (page - 1) * perPage;

  return {
    rows: perPage > 0 ? rows.slice(inicio, inicio + perPage) : rows,
    total: rows.length,
    page,
    perPage,
    totais,
  };
};

export default { obterApuracao, listarApuracao, BENEFICIOS, ROTULOS_GRUPO, trabalhosDoProjeto };
